import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { GridFilterItem } from "@mui/x-data-grid";
import { debounce } from "lodash";

import { CUSTOM_PANELS } from "helpers/apis";
import useAxios from "helpers/axios";
import { ICustomPanel } from "types/panel";

export interface IDraftCustomPanelContext {
  currentPage: number;
  draftCustomPanels: ICustomPanel[];
  loading: boolean;
  message: DraftCustomPanelsMessageType | null;
  totalCount: number;
  setCurrentPage: Function;
  fetch: (currentPage: number, filter?: GridFilterItem) => void;
  setDraftCustomPanels: React.Dispatch<React.SetStateAction<ICustomPanel[]>>;
  setMessage: React.Dispatch<
    React.SetStateAction<DraftCustomPanelsMessageType | null>
  >;
}

export const DraftCustomPanelContext = createContext<IDraftCustomPanelContext>(
  {} as IDraftCustomPanelContext
);

type DraftCustomPanelsMessageType =
  | "delete_succeed"
  | "delete_failed"
  | "ownership_transferred";

export function DraftCustomPanelProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [draftCustomPanels, setDraftCustomPanels] = useState<ICustomPanel[]>(
    []
  );
  const [message, setMessage] = useState<DraftCustomPanelsMessageType | null>(
    null
  );
  const { fetchData, loading } = useAxios({
    config: { url: CUSTOM_PANELS },
    callOnInit: false,
  });
  const { current: debouncedFetch } = useRef(
    debounce((currentPage, filter?: GridFilterItem) => {
      fetchData(
        {
          params: {
            pageNumber: currentPage,
            pageSize: 20,
            status: "draft",
            ...(filter
              ? {
                  filter: JSON.stringify({
                    id: filter.id,
                    column_field: filter.field,
                    operator_value: filter.operator,
                    value: filter.value,
                  }),
                }
              : {}),
          },
        },
        ({
          response: { data, totalCount: newTotalCount },
        }: {
          response: {
            data: Array<ICustomPanel>;
            totalCount: number;
          };
        }) => {
          if (Array.isArray(data)) {
            setDraftCustomPanels(data);
            setTotalCount(newTotalCount);
          } else {
            setDraftCustomPanels([]);
            setTotalCount(0);
            setCurrentPage(1);
          }
        }
      );
    }, 500)
  );

  const memoedValue = useMemo(
    () => ({
      currentPage,
      draftCustomPanels,
      loading,
      message,
      totalCount,
      fetch: debouncedFetch,
      setCurrentPage,
      setDraftCustomPanels,
      setMessage,
    }),
    [
      currentPage,
      debouncedFetch,
      draftCustomPanels,
      loading,
      message,
      totalCount,
    ]
  );

  useEffect(() => {
    if (debouncedFetch) {
      debouncedFetch(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFetch]);

  return (
    <DraftCustomPanelContext.Provider value={memoedValue}>
      {children}
    </DraftCustomPanelContext.Provider>
  );
}

export default function useDraftCustomPanels() {
  return useContext(DraftCustomPanelContext);
}
